import { useRouter } from '@tanstack/react-router';
import { Stack, Text, Grid, InlineStack, Box } from '@customerio/pluma-components/react';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { getIntegrationActions, getManualActions } from '../actions';
import { AddPeopleActionCard } from './add-people-action-card';

type AddPeopleActionsProps = {
  /**
   * Hides the integrations grid and swaps the manual section's lead-in, matching
   * Ember's `billing.isBuilder` branch in `add-people/index.gts`. Off by default:
   * `/home/add-people` has never gated on the plan, and turning it on there would
   * change live onboarding.
   */
  isBuilder?: boolean;
  /** Fired with the card's title (or `'Other'` for the untitled catch-all card). */
  onActionClick: (title: string) => void;
};

/**
 * The add-people catalog: an integrations grid (unless the account is on the
 * Builder plan) above a one-time-import grid. Shared by the onboarding page at
 * `/home/add-people` and the migrated `/profiles/create`.
 */
export function AddPeopleActions({ isBuilder = false, onActionClick }: AddPeopleActionsProps) {
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const taxonomy = useProductTaxonomy();
  const router = useRouter();

  const integrationActions = getIntegrationActions(
    workspaceId,
    taxonomy,
    (source) =>
      router.buildLocation({
        to: '/workspaces/$workspaceId/pipelines/sources/add',
        params: { workspaceId },
        search: { source },
      }).href,
  );
  // The add-a-person form is Hydra's only behind `profiles-migration`; with the flag off the
  // embedded app still serves it at the `journeys` URL, so the card links there directly.
  const isProfilesMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const addPersonHref = isProfilesMigrated
    ? router.buildLocation({ to: '/workspaces/$workspaceId/profiles/new', params: { workspaceId } }).href
    : generatePath('/workspaces/:workspaceId/journeys/people/new', { workspaceId }, { type: 'people' });
  const manualActions = getManualActions(workspaceId, taxonomy, addPersonHref);

  return (
    <Stack gap="200">
      {!isBuilder ? (
        <Box as="section" data-test="add-people-integration-section">
          <Grid columns={3} gap="200">
            {integrationActions.map((action, index) => (
              <AddPeopleActionCard key={action.title ?? index} action={action} onClickAction={onActionClick} />
            ))}
          </Grid>
        </Box>
      ) : null}

      <Box as="section" data-test="add-people-manual-section">
        <InlineStack mb="100">
          <Text color="subtle">
            {isBuilder
              ? `Add ${taxonomy.term('people.plural')} with a one-time import:`
              : 'Alternatively, try it out with a one-time import:'}
          </Text>
        </InlineStack>
        <Grid columns={3} gap="200">
          {manualActions.map((action) => (
            <AddPeopleActionCard key={action.title} action={action} onClickAction={onActionClick} />
          ))}
        </Grid>
      </Box>
    </Stack>
  );
}
